import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Dumbbell, Search, ExternalLink, Play } from "lucide-react";

const DIFFICULTY_COLORS = {
  débutant: "bg-green-50 text-green-700",
  intermédiaire: "bg-amber-50 text-amber-700",
  avancé: "bg-red-50 text-red-700",
};

export default function ExerciseLibrary() {
  const [exercises, setExercises] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [muscleGroup, setMuscleGroup] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [selected, setSelected] = useState(null);

  const fetchExercises = () => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (muscleGroup !== "all") params.muscle_group = muscleGroup;
    if (difficulty !== "all") params.difficulty = difficulty;

    api.get("/exercises", { params })
      .then(r => setExercises(r.data.exercises || []))
      .catch(() => setExercises([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/exercises/muscle-groups").then(r => setMuscleGroups(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchExercises, 300);
    return () => clearTimeout(t);
  }, [search, muscleGroup, difficulty]);

  const grouped = exercises.reduce((acc, ex) => {
    const g = ex.muscle_group || "Autre";
    if (!acc[g]) acc[g] = [];
    acc[g].push(ex);
    return acc;
  }, {});

  return (
    <div data-testid="exercise-library-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
          <Dumbbell className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Bibliothèque d'Exercices</h1>
          <p className="text-slate-400 mt-1 text-sm">{exercises.length} exercice{exercises.length !== 1 ? "s" : ""} disponible{exercises.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un exercice..."
            className="pl-9"
          />
        </div>
        <Select value={muscleGroup} onValueChange={setMuscleGroup}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Groupe musculaire" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les groupes</SelectItem>
            {muscleGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={difficulty} onValueChange={setDifficulty}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Difficulté" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="débutant">Débutant</SelectItem>
            <SelectItem value="intermédiaire">Intermédiaire</SelectItem>
            <SelectItem value="avancé">Avancé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : exercises.length === 0 ? (
        <div className="text-center py-16 text-slate-400">Aucun exercice trouvé</div>
      ) : (
        <div className="mt-6 space-y-8">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([group, items]) => (
            <div key={group}>
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 uppercase tracking-tight">{group}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(ex => (
                  <Card
                    key={ex.exercise_id}
                    className="border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => setSelected(ex)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-slate-900 text-sm">{ex.name}</p>
                        <Badge className={`text-xs shrink-0 ${DIFFICULTY_COLORS[ex.difficulty] || "bg-slate-100"}`}>
                          {ex.difficulty}
                        </Badge>
                      </div>
                      {ex.equipment && <p className="text-xs text-slate-400 mt-1">{ex.equipment}</p>}
                      {ex.description && (
                        <p className="text-xs text-slate-500 mt-2 line-clamp-2">{ex.description}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={v => !v && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{selected?.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2">
              <div className="flex flex-wrap gap-2">
                <Badge>{selected.muscle_group}</Badge>
                <Badge variant="outline">{selected.difficulty}</Badge>
                {selected.equipment && <Badge variant="secondary">{selected.equipment}</Badge>}
              </div>
              {selected.photo_url && (
                <img src={selected.photo_url} alt={selected.name} className="w-full rounded-xl object-cover max-h-48" />
              )}
              {selected.description && (
                <p className="text-sm text-slate-600 leading-relaxed">{selected.description}</p>
              )}
              {selected.video_url && (
                <Button asChild variant="outline" className="w-full gap-2">
                  <a href={selected.video_url} target="_blank" rel="noreferrer">
                    <Play className="w-4 h-4" /> Voir la vidéo <ExternalLink className="w-3 h-3" />
                  </a>
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}